/**
 * The card list of the validate shipment details screen: one card per group of shipment settings,
 * each opening in place, with the expand/collapse-all control above them. The card bodies are
 * filled in by the slices that own them; when the list may be shown at all is the screen's call.
 */
import { observer } from "mobx-react";
import { ExpandCollapseAll } from "../../../../../../../components/expand-collapse-all/expand-collapse-all";
import FontBody1 from "../../../../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../../components/fonts/font-props";
import IconEye from "../../../../../../../components/icons/icon-eye/icon-eye";
import IconReserveItem from "../../../../../../../components/icons/icon-reserve-item/icon-reserve-item";
import IconShipmentReceived from "../../../../../../../components/icons/icon-shipment-received/icon-shipment-received";
import IconUser from "../../../../../../../components/icons/icon-user/icon-user";
import { IconProps } from "../../../../../../../components/icons/icon-props-interface";
import Icon from "../../../../../../../components/icons/icon/icon";
import ListLayout, { LIST_LAYOUT_SPACING_VARIANT } from "../../../../../../../components/layouts/list-layout/list-layout";
import useExpandCollapse from "../../../../../../../hooks/expand-collapse-hook";
import ResolveLabelErrorCard from "../../resolve-label-error-card/resolve-label-error-card";
import CustomerAddress from "./customer-address/customer-address";
import ParcelDetails from "./parcel-details/parcel-details";
import ReturnAddress from "./return-address/return-address";
import { IValidateShippingDetailsProps } from "./validate-shipping-details-props";
import "./validate-shipping-details-cards.scss";

type ValidateShippingDetailsCard = {
    id: string;
    icon: React.FC<IconProps>;
    title: string;
    description: string;
    /** Filled in by the slice that owns the section. */
    Content: React.FC<IValidateShippingDetailsProps>;
};

const CARDS: ValidateShippingDetailsCard[] = [
    {
        id: "customer-address",
        icon: IconUser,
        title: "Customer Address (Sender address)",
        description: "The returning customer's details",
        Content: CustomerAddress,
    },
    {
        id: "return-address",
        icon: IconShipmentReceived,
        title: "Return Address (Recipient address)",
        description: "The details of returned point for sending back the items",
        Content: ReturnAddress,
    },
    {
        id: "parcel-details",
        icon: IconReserveItem,
        title: "Parcel Details",
        description: "Full details of the packages that is planed to be sent",
        Content: ParcelDetails,
    },
];

const ValidateShippingDetailsCards: React.FC<IValidateShippingDetailsProps> = observer(({ store, validationGroup }) => {
    const expandCollapse = useExpandCollapse();

    /**
     * Read every card before aggregating: getIsExpanded seeds the map on first read, and
     * Array.every would short-circuit before the later cards were ever seeded.
     */
    const expandedFlags = CARDS.map((card) => expandCollapse.getIsExpanded(card.id));
    const isAllExpanded = expandedFlags.every(Boolean);

    /** A section counts as reviewed once it has been put in front of the merchant. */
    const handleCardExpand = (cardId: string, isExpanded: boolean) => {
        expandCollapse.setExpanded(cardId, isExpanded);
        if (isExpanded) {
            store.markCardReviewed(cardId);
        }
    };

    const handleToggleAll = () => {
        expandCollapse.toggleAllExpanded();
        if (!isAllExpanded) {
            CARDS.forEach((card) => store.markCardReviewed(card.id));
        }
    };

    return (
        <div className="validate-shipping-details-cards">
            <ListLayout spacingVariant={LIST_LAYOUT_SPACING_VARIANT.SMAll}>
                <FontBody1 color={FONT_COLOR.NEUTRAL12} variant={[FONT_VARIANT.BOLD]}>
                    Validate Shipping Details:
                </FontBody1>

                <div className="validate-shipping-details-cards-toolbar">
                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>Review and validate all details</FontBody2>
                    <div className="validate-shipping-details-cards-toolbar-expand">
                        <ExpandCollapseAll isAllExpanded={isAllExpanded} onClick={handleToggleAll} />
                    </div>
                </div>

                {CARDS.map(({ id, icon, title, description, Content }, index) => {
                    const isExpanded = expandedFlags[index];
                    // The marker only means something while the section is closed.
                    const isReviewed = !isExpanded && store.isCardReviewed(id);

                    return (
                        <ResolveLabelErrorCard
                            key={id}
                            icon={icon}
                            title={title}
                            description={description}
                            trailing={isReviewed
                                ? <Icon Component={IconEye} color="var(--color-neutral-3)" />
                                : undefined}
                            isExpanded={isExpanded}
                            onClick={() => handleCardExpand(id, !isExpanded)}
                        >
                            <Content store={store} validationGroup={validationGroup} />
                        </ResolveLabelErrorCard>
                    );
                })}
            </ListLayout>
        </div>
    );
});

export default ValidateShippingDetailsCards;
